import React, { useId } from 'react';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import { XCircleIcon } from '@heroicons/react/24/outline';
import { twJoin } from 'tailwind-merge';
import {
  RichTextFragment,
  TileFragmentFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { CrystallizeContentHelper } from '@/utils/crystallize-content-helper';

interface AdditionalInfoBoxProps {
  additionalInformation: {
    additionalInformationText?: RichTextFragment;
    additionalInformationTiles?: TileFragmentFragment[];
  };
  activeBoxId?: string;
  onSelect: (boxId: string) => void;
  onClear: () => void;
  screenReaderText?: string;
}

export function AdditionalInformationBox({
  additionalInformation,
  activeBoxId,
  onSelect,
  onClear,
  screenReaderText,
}: AdditionalInfoBoxProps) {
  const boxId = useId();
  const { additionalInformationText, additionalInformationTiles } = additionalInformation;

  const hasContent = !CrystallizeContentHelper.isRichTextEmpty(additionalInformationText);
  const hasTiles = additionalInformationTiles && additionalInformationTiles.length > 0;
  const isActive = boxId === activeBoxId;

  const handleOnClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onSelect(boxId);
  };

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      if (e.target !== e.currentTarget) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      onSelect(boxId);
    }
  };

  if (!hasContent) {
    return null;
  }

  return hasTiles ? (
    <div
      role="button"
      tabIndex={0}
      aria-label={screenReaderText || 'Additional information'}
      className="mt-5 w-full rounded-xl border border-neutral-10 bg-neutral-5 px-4 py-2 cursor-pointer"
      onClick={handleOnClick}
      onKeyDown={handleOnKeyDown}
    >
      <div className="flex justify-between text-start">
        <div><RichTextRenderer blocks={additionalInformationText} /></div>
        <button
          type="button"
          aria-expanded={isActive}
          className="-mr-2 flex h-6 w-6 overflow-hidden"
          onClick={(e) => {
            e.stopPropagation();
            if (isActive) {
              onClear();
            } else {
              onSelect(boxId);
            }
          }}
        >
          <p className="sr-only">
            {isActive ? 'Close additional info' : 'Open additional info'}
          </p>
          <XCircleIcon
            className={twJoin(
              'h-6 w-6 transition-transform duration-200 ease-in',
              !isActive && 'rotate-45',
            )}
          />
        </button>
      </div>
    </div>
  ) : (
    <div className="mt-5 w-full rounded-xl border border-neutral-10 bg-neutral-5 px-4 py-2">
      <p className="sr-only">{screenReaderText || 'Additional information'}</p>
      <div className="flex justify-between text-start">
        <div><RichTextRenderer blocks={additionalInformationText} /></div>
      </div>
    </div>
  );
}
